// BroadPictureAlerts.jsx
// DAS-2681: the "Broad Picture" tab in the Alerts modal — top-down exposure
// warnings (scope 'Broad_Picture'): your position/region vs a RISING named risk
// on the forward-risk watchlist (DAS-2663/2666/2674). Separate from the
// bottom-up Portfolio Alerts tab on purpose: different lifecycle (created and
// retired by the daily Broad Picture run, not the nightly detection cycle) and
// region rows have no ticker, which the Portfolio panel's query cannot render.
//
// Reads GET /api/broad_picture_alerts/<userId>; seen/dismiss ride the existing
// POST /api/pf_alert_check_update (dismiss = bignore=1, which holds until the
// risk stops rising — a later re-rise creates a fresh alert).
//
// DAS-2960: one card per position/region. The same subject used to get one
// card per rising risk (QDVE.DE showed three times); the risks are now stacked
// inside the subject's single card, each with its own seen/dismiss lifecycle.

import React, { useEffect, useState, useCallback } from 'react';
import { Card, Tag, Typography, Spin, Alert, Button, Popconfirm, Checkbox, Empty, message, Collapse } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import api from '../functions/api';
import getUserId from '../functions/getUserId';

const { Text, Paragraph } = Typography;

// DAS-2894: the severity phrase — plain words for the three ordering
// ingredients (tier, impact, exposure). The backend sorts; this explains why.
// Tier and impact come from the LATEST daily run (refreshed live), the
// exposure from today's holdings.
const severityPhrase = (a) => {
  const parts = [];
  if (a.severity_tier) parts.push(`${a.severity_tier} risk`);
  if (a.severity_impact) parts.push(`${a.severity_impact} impact`);
  if (a.exposure_pct != null) parts.push(`${a.exposure_pct}% of investable holdings`);
  return parts.join(' · ');
};

const fmtEpoch = (epoch) => {
  if (!epoch) return '';
  try { return new Date(epoch * 1000).toLocaleDateString(); } catch { return ''; }
};

// DAS-2960: days since inception, shown next to the date.
const daysSince = (epoch) => {
  if (!epoch) return null;
  const d = Math.floor((Date.now() / 1000 - epoch) / 86400);
  return d >= 0 ? d : null;
};

// DAS-2960: pull the per-risk essentials out of the generator's fixed text
// shape (scenario_offside_alerts.build_offside_text / build_region_offside_text).
// Falls back to nothing extracted — the full text is always available below.
export const parseRiskBits = (text) => {
  const name = /risk '(.+?)' is RISING/.exec(text || '')?.[1] || null;
  const why = /Why the risk is rising: (.+)/.exec(text || '')?.[1] || null;
  return { name, why };
};

// DAS-2960: group the flat rows per subject (ticker, else region), preserving
// the server's order (unseen first, then severity — DAS-2906/2960): a group
// sits where its best-placed row sits.
export const groupBySubject = (alerts) => {
  const groups = [];
  const byKey = new Map();
  for (const a of alerts) {
    const key = a.ticker ? `t:${a.ticker}` : `r:${a.region || '?'}`;
    if (!byKey.has(key)) {
      const g = { key, ticker: a.ticker, company: a.company, region: a.region, rows: [] };
      byKey.set(key, g);
      groups.push(g);
    }
    byKey.get(key).rows.push(a);
  }
  return groups;
};

const BroadPictureAlerts = ({ onUnseenChange }) => {  // DAS-2939: tab tells its badge
  // DAS-2557/2620: prefer the reactive auth userId; getUserId() lags on first login.
  const authUserId = useSelector(state => state?.auth?.userId);
  const userId = authUserId || getUserId();

  const [alerts, setAlerts] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!userId) return;
    api.get(`/broad_picture_alerts/${userId}`)
      .then(res => {
        const list = res.data?.alerts || [];
        setAlerts(list);
        // DAS-2939: every (re)load — incl. after seen/dismiss saves — updates the tab dot
        if (onUnseenChange) onUnseenChange(list.some(a => !a.seen));
      })
      .catch(err => setError(err?.response?.data?.error || err.message));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, fields, okMsg) => {
    try {
      await api.post('/pf_alert_check_update', { id, ...fields });
      if (okMsg) message.success(okMsg);
      load();
    } catch (err) {
      message.error('Update failed: ' + (err?.response?.data?.message || err.message));
    }
  };

  if (error) return <Alert type="error" message="Could not load Broad Picture alerts" description={error} />;
  if (alerts === null) return <Spin style={{ display: 'block', margin: '40px auto' }} />;
  if (!alerts.length) {
    return <Empty description="No open exposure warnings — no rising watchlist risk currently hits your positions or regions." />;
  }

  const groups = groupBySubject(alerts);

  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Top-down warnings from the Broad Picture risk watchlist: a position or region of yours
        is material while a named risk is RISING — one card per position/region, all its rising
        risks inside, unseen first, then most severe first. They disappear by themselves when
        the risk calms down; dismissing hides one until the risk rises again.
      </Paragraph>
      {groups.map(g => {
        const isRegion = !g.ticker;
        const first = g.rows[0]; // best-placed row: carries the group's top severity
        const earliest = Math.min(...g.rows.map(r => r.epoch_initiation || Infinity));
        const days = daysSince(earliest === Infinity ? null : earliest);
        const allSeen = g.rows.every(r => r.seen);
        return (
          <Card key={g.key} size="small" data-testid="das2960-group"
                style={{ marginBottom: 10, opacity: allSeen ? 0.75 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Text strong>
                {isRegion
                  ? (<span><GlobalOutlined /> {g.region || 'Region'}</span>)
                  : `${g.ticker}${g.company ? ` — ${g.company}` : ''}`}
              </Text>
              <Tag color={isRegion ? 'geekblue' : 'volcano'}>
                {isRegion ? 'Region exposure' : 'Position exposure'}
              </Tag>
              {g.rows.length > 1 && (
                <Tag color="orange" data-testid="das2960-risk-count">{g.rows.length} rising risks</Tag>
              )}
              <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
                since {fmtEpoch(earliest === Infinity ? null : earliest)}
                {days != null && <span data-testid="das2960-days"> ({days}d)</span>}
              </Text>
            </div>
            {severityPhrase(first) && (
              <div data-testid="das2894-severity" style={{ fontSize: 12.5, color: '#8c5e00', marginTop: 4 }}>
                {severityPhrase(first)}
              </div>
            )}
            {g.rows.map((a, i) => {
              const bits = parseRiskBits(a.pf_alert_text);
              const riskDays = daysSince(a.epoch_initiation);
              return (
                <div key={a.id} data-testid="das2960-risk"
                     style={{ marginTop: 10, paddingTop: i > 0 ? 10 : 0,
                              borderTop: i > 0 ? '1px solid #f0f0f0' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Text strong style={{ fontSize: 13 }}>
                      ⚠ {bits.name || (a.alert_subtype || 'Rising risk')}
                    </Text>
                    {a.severity_tier && <Tag>{a.severity_tier}</Tag>}
                    {a.severity_impact && <Tag color={a.severity_impact === 'severe' ? 'red' : undefined}>{a.severity_impact}</Tag>}
                    {riskDays != null && (
                      <Text type="secondary" style={{ fontSize: 12 }}>since {fmtEpoch(a.epoch_initiation)} ({riskDays}d)</Text>
                    )}
                  </div>
                  {bits.why && (
                    <Paragraph style={{ margin: '4px 0 0 0', fontSize: 13 }}>
                      Why the risk is rising: {bits.why}
                    </Paragraph>
                  )}
                  <Collapse ghost size="small" items={[{
                    key: 'full',
                    label: <span style={{ fontSize: 12 }}>full alert text</span>,
                    children: <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{a.pf_alert_text}</Paragraph>,
                  }]} />
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <Checkbox
                      checked={!!a.seen}
                      onChange={e => update(a.id, { seen: e.target.checked ? 1 : 0 })}
                    >
                      Seen
                    </Checkbox>
                    <Checkbox
                      checked={!!a.followed_up}
                      onChange={e => update(a.id, { followed_up: e.target.checked ? 1 : 0 })}
                    >
                      Followed up
                    </Checkbox>
                    <Popconfirm
                      title="Dismiss this warning? It stays hidden until the risk stops rising and later rises again."
                      onConfirm={() => update(a.id, { bignore: 1 }, 'Dismissed')}
                      okText="Dismiss"
                    >
                      <Button size="small" danger style={{ marginLeft: 'auto' }}>Dismiss</Button>
                    </Popconfirm>
                  </div>
                </div>
              );
            })}
          </Card>
        );
      })}
    </div>
  );
};

export default BroadPictureAlerts;
